import React, { useMemo } from 'react';
import { View, Text, Image, StyleSheet } from 'react-native';
import { useTheme, ThemeColors } from '../../context/ThemeContext';
import { loaderLook } from '../../utils/loaderLook';

interface Props {
  size?: 'small' | 'large';
  message?: string;
  fullScreen?: boolean;
}

// The web build reaches the browser through `any` - this project compiles without the DOM lib.
const web: any = globalThis as any;
const prefersReducedMotion = () => !!web.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

const crestImage = require('../../assets/images/cayman_icon.png');

// CSS keyframes (react-native-web's animationKeyframes, as its own ActivityIndicator uses). The
// browser runs them itself, so the loader keeps moving while the page's JavaScript is busy
// loading - which is exactly when it is on screen. They must go through StyleSheet.create:
// react-native-web only writes the @keyframes rule for a compiled style, and an inline one
// silently gets no animation at all.
const rippleKeyframes = [{ '0%': { transform: 'scale(0.9)', opacity: 0.9 }, '100%': { transform: 'scale(1.9)', opacity: 0 } }];
const motion = StyleSheet.create({
  breathe: {
    animationKeyframes: [{ '0%': { transform: 'scale(0.96)' }, '50%': { transform: 'scale(1.04)' }, '100%': { transform: 'scale(0.96)' } }],
    animationDuration: '2.2s',
    animationTimingFunction: 'ease-in-out',
    animationIterationCount: 'infinite',
  },
  ripple: {
    animationKeyframes: rippleKeyframes,
    animationDuration: '2.2s',
    animationTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
    animationIterationCount: 'infinite',
  },
  // Negative: the second ring starts half way through, so both are moving from the first frame.
  rippleLate: {
    animationKeyframes: rippleKeyframes,
    animationDuration: '2.2s',
    animationTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
    animationIterationCount: 'infinite',
    animationDelay: '-1.1s',
  },
} as any);

/**
 * The web portal's loader (design B, chosen 2 Nov 2026): the school crest breathing while soft
 * rings ripple out (utils/loaderLook). The phone app keeps LoadingSpinner.tsx.
 *
 * The standard, everywhere on the web portal:
 * - The portal starting or signing in: `fullScreen` - the crest on the navy band.
 * - A page's first load: the page's own WebPageShell (header and sidebar stay) with
 *   `<LoadingSpinner message="Loading <page>..." />` inside it.
 * - A section, list, tab or popup loading: `<LoadingSpinner message=... />`, or `size="small"`
 *   in a small panel.
 * - Inside a button or beside a label (saving, downloading, a photo uploading): a small
 *   ActivityIndicator - the crest is too big for those.
 */
export function LoadingSpinner({ size = 'large', message, fullScreen = false }: Props) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const look = loaderLook({ size, fullScreen, reducedMotion: prefersReducedMotion() });
  const band = useMemo(
    () => ({ backgroundImage: `linear-gradient(135deg, ${colors.headerFrom} 0%, ${colors.headerTo} 100%)` }) as any,
    [colors],
  );

  const ringInset = (look.stage - look.disc) / 2;
  const ringStyle = {
    position: 'absolute' as const,
    top: ringInset,
    left: ringInset,
    width: look.disc,
    height: look.disc,
    borderRadius: look.disc / 2,
    borderWidth: look.ringWidth,
    borderColor: look.onBand ? 'rgba(255, 255, 255, 0.55)' : 'rgba(27, 112, 209, 0.45)',
  };

  return (
    <View
      style={[styles.container, { minHeight: look.minHeight }, look.onBand && styles.fullScreen, look.onBand && band]}
      accessibilityRole="progressbar"
      accessibilityLabel={message ?? 'Loading'}
    >
      <View style={{ width: look.stage, height: look.stage, alignItems: 'center', justifyContent: 'center' }}>
        {look.rings > 0 && <View style={[ringStyle, motion.ripple]} />}
        {look.rings > 1 && <View style={[ringStyle, motion.rippleLate]} />}
        <View
          style={[
            styles.disc,
            look.onBand ? styles.discOnBand : styles.discOnPage,
            { width: look.disc, height: look.disc, borderRadius: look.disc / 2 },
            look.breathe && motion.breathe,
          ]}
        >
          <Image source={crestImage} style={{ width: look.crest, height: look.crest }} resizeMode="contain" />
        </View>
      </View>
      {!!message && (
        <Text style={[styles.message, { fontSize: look.messageSize }, look.onBand && styles.messageOnBand]}>
          {message}
        </Text>
      )}
    </View>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  fullScreen: {
    flex: 1,
    backgroundColor: colors.headerFrom,
  },
  disc: {
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  discOnBand: {
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.25,
    shadowRadius: 24,
  },
  discOnPage: {
    borderWidth: 1,
    borderColor: colors.border,
    shadowColor: '#032158',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.12,
    shadowRadius: 16,
  },
  message: {
    marginTop: 10,
    fontWeight: '600',
    letterSpacing: 0.2,
    color: colors.textSecondary,
  },
  messageOnBand: {
    color: colors.headerTextMuted,
  },
});
